import { apiClient } from './client'
import type { LabelTemplateAssignment, LabelTemplateSummary } from '@/types'
import type { LabelCodeType } from '@/types/label-template'

export const statsApi = {
  get: () =>
    apiClient.get<{
      nomenclature_count: number
      labels_today: number
      labels_total: number
      tasks_total: number
      tasks_new: number
      tasks_active: number
      tasks_completed: number
      shipments_total: number
    }>('/api/stats'),
}

export interface SidebarNavItem {
  icon: string
  label: string
  path: string
  match: string[]
}

export interface SidebarCatalogItem {
  icon: string
  label: string
  endpoint: string
  match: string[]
  path: string
  group: string
  group_label: string
  enabled: boolean
}

export const sidebarApi = {
  get: (role?: string) =>
    apiClient.get<SidebarNavItem[]>('/api/sidebar', { params: role ? { role } : undefined }),
  getCatalog: (role: 'admin' | 'worker') =>
    apiClient.get<SidebarCatalogItem[]>('/api/sidebar/catalog', { params: { role } }),
  saveConfig: (data: { admin: object[]; worker: object[] }) =>
    apiClient.put('/api/advanced/sidebar', data),
}

export interface PerformanceSettings {
  max_workers_km: number
  max_workers_boxes?: number
}

export interface GeneralSettings {
  global_print_mode?: string
  print_method?: 'agent' | 'browser'
  scan_duplicate_mode?: 'reject' | 'highlight'
  performance?: PerformanceSettings
  cpu_count?: number
  gs1_company_prefix?: string
}

export const settingsApi = {
  get: () => apiClient.get('/api/settings'),
  save: (data: Record<string, unknown>) => apiClient.put('/api/settings', data),
  getPrinters: () => apiClient.get('/api/printers'),
  getDbSettings: () => apiClient.get('/api/settings/database'),
  saveDbSettings: (data: Record<string, unknown>) => apiClient.put('/api/settings/database', data),
  testMongo: (data: { uri: string; db_name: string }) => apiClient.post('/api/settings/database/test', data),
  testPostgres: (data: { uri: string }) => apiClient.post('/api/settings/database/test-postgres', data),
  testRedis: (data: { url: string }) => apiClient.post('/api/settings/database/test-redis', data),
  migrate: (data: { uri: string; db_name: string; data_dir?: string }) =>
    apiClient.post('/api/settings/database/migrate', data),
  // Feature flags
  getFeatures: () => apiClient.get<FeatureFlags>('/api/settings/features'),
  saveFeatures: (flags: FeatureFlags) => apiClient.put<FeatureFlags>('/api/settings/features', flags),
  getFeature: (name: string) => apiClient.get<{ feature: string; enabled: boolean }>(`/api/settings/features/${name}`),
}

export interface FeatureFlags {
  enable_tasks: boolean
  enable_shipments: boolean
  auto_create_shipment_on_complete: boolean
  enable_ka_generation: boolean
  enable_in_task_aggregation: boolean
  enable_separate_km_ka_print: boolean
  scan_ewk_batch_endpoint: boolean
  enable_task_extra_attribute: boolean
}

export interface FsEntry {
  name: string
  path: string
  is_dir: boolean
}

export interface BrowseResult {
  path: string
  parent: string | null
  items: FsEntry[]
}

export const filesystemApi = {
  browse: (path?: string) =>
    apiClient.get<BrowseResult>('/api/filesystem/browse', { params: path ? { path } : undefined }),
}

export const templatesApi = {
  getAll: () => apiClient.get<LabelTemplateSummary[]>('/api/templates'),

  getDataFields: () => apiClient.get<LabelDataField[]>('/api/templates/data-fields'),

  getById: (name: string) => apiClient.get(`/api/templates/${encodeURIComponent(name)}`),

  create: (data: Record<string, unknown>) =>
    apiClient.post<{ filename: string; template_id: string }>('/api/templates', data),

  save: (name: string, data: Record<string, unknown>) =>
    apiClient.put(`/api/templates/${encodeURIComponent(name)}`, data),

  updateAssignment: (name: string, data: LabelTemplateAssignment) =>
    apiClient.patch<LabelTemplateSummary>(`/api/templates/${encodeURIComponent(name)}`, data),

  delete: (name: string) => apiClient.delete(`/api/templates/${encodeURIComponent(name)}`),

  renderPreview: (template: Record<string, unknown>, data: Record<string, unknown>) =>
    apiClient.post<LabelPreviewResponse>('/api/preview/render', { template, data }),
}

export interface LabelPreviewResponse {
  image: string
  render_mode?: string
  warnings?: string[]
}

export interface LabelDataField {
  key: string
  label: string
  source: string
  sources?: Partial<Record<LabelCodeType, string>>
}

// ─── KA Templates (структура кода агрегации) ─────────────────────────────────

// GS1 Application Identifier — справочная запись
export interface GS1AIEntry {
  ai: string           // код: "00", "01", "10", "3102", ...
  title_ru: string     // русское наименование
  title_en: string     // английское наименование (official GS1)
  field_type: 'N' | 'X' | 'Y'  // N=числовой, X=алфавитно-цифровой, Y=CPID
  fixed: number | null          // фиксированная длина (null если переменная)
  max_length: number | null     // максимальная длина (null если фиксированная)
  fnc1_after: boolean           // нужен FNC1/GS разделитель при переменной длине
  category: string              // логическая группа
  format: string                // строка формата, напр. «N18», «X..20»
}

export const gs1AiApi = {
  getAll: () =>
    apiClient.get<{ success: boolean; data: GS1AIEntry[] }>('/api/gs1/ai-catalog'),
}

export interface KAAIEntry {
  ai: string        // код AI: "00", "02", "37", "10", ...
  label: string     // читаемое название
  source: string    // "generated" | "generated.ai21" | "manual" (legacy task.* → manual at generate)
  required: boolean // всегда включать в код
  fnc1_after?: boolean // вставить FNC1 (Group Separator) после этого AI
  default_value?: string // значение по умолчанию (для source='manual' и других — подставляется если не переопределено при создании задания)
}

export interface KATemplate {
  template_id: string
  template_name: string
  template_type: 'ka_box' | 'ka_pallet'
  description: string
  extension_digit: string  // '0' = коробка, '1' = паллета
  ai_list: KAAIEntry[]     // состав и порядок Application Identifiers
  fnc1_positions: number[] // 1-based позиции вставки GS (\x1d) в чистой строке
  is_default: boolean
  is_active: boolean
  created_at?: string
  updated_at?: string
}

export const kaTemplatesApi = {
  getAll: () =>
    apiClient.get<KATemplate[]>('/api/ka-templates'),
  getById: (id: string) =>
    apiClient.get<KATemplate>(`/api/ka-templates/${encodeURIComponent(id)}`),
  create: (data: Partial<KATemplate>) =>
    apiClient.post<{ template_id: string }>('/api/ka-templates', data),
  update: (id: string, data: Partial<KATemplate>) =>
    apiClient.put(`/api/ka-templates/${encodeURIComponent(id)}`, data),
  delete: (id: string) =>
    apiClient.delete(`/api/ka-templates/${encodeURIComponent(id)}`),
  getByType: (type: 'ka_box' | 'ka_pallet') =>
    apiClient.get<KATemplate[]>(`/api/ka-templates/by-type/${type}`),
}

export const jobsApi = {
  getAll: (params?: { type?: string; limit?: number }) =>
    apiClient.get('/api/jobs', { params }),
  getById: (id: string) => apiClient.get(`/api/jobs/${id}`),
  cancel: (id: string) => apiClient.post(`/api/jobs/${id}/cancel`),
}
